import { useMemo, useContext } from "react";
import { WalletData } from "@swiggy-private/payment-ui";
import { VALID_WALLET_LINK_STATUSES, WalletApiFetchState } from "@swiggy-private/payment-core";
import { WalletBalanceContext } from "@payment-client/contexts/WalletBalanceContext";
import { WalletBalanceStateValue } from "@payment-client/reducers/Wallet";
import { round } from "@payment-client/utils/Math";

export const defaultWalletData = {
    linkStatus: VALID_WALLET_LINK_STATUSES.NEVER_LINKED,
    balance: 0,
    additionalBalanceRequired: 0,
    linked: false,
    isWalletBalanceSufficent: false,
    isOldWalletLinkFlow: false,
    mobileNumber: "",
};

function buildWalletData(state: WalletBalanceStateValue, paymentAmount: number): WalletData {
    const { balance, linkStatus, isOldWalletLinkFlow, mobileNumber, paymentMethodDisabled } = state;
    const toPay = state.payableAmount ? Number(state.payableAmount) : paymentAmount;
    const additionalBalanceRequired = balance >= toPay ? 0 : round(toPay - balance);

    const data: WalletData = {
        ...defaultWalletData,
        balance,
        linkStatus,
        additionalBalanceRequired,
        isWalletBalanceSufficent: additionalBalanceRequired === 0,
        linked: linkStatus === VALID_WALLET_LINK_STATUSES.LINKED,
        isOldWalletLinkFlow,
        mobileNumber,
        paymentMethodDisabled,
    };

    return data;
}

interface WalletBalanceDataProps {
    paymentAmount: number;
}

interface WalletBalanceDataReturnType {
    [key: string]: {
        fetchState: WalletApiFetchState;
        data: WalletData;
        refetch: () => Promise<void>;
    };
}

export function useWalletBalanceData({ paymentAmount }: WalletBalanceDataProps): WalletBalanceDataReturnType {
    const { state, refetch } = useContext(WalletBalanceContext);
    return useMemo(
        () =>
            Object.keys(state).reduce<WalletBalanceDataReturnType>((acc, name) => {
                const _state = state[name];
                acc[name] = {
                    fetchState: _state.fetchState,
                    data: buildWalletData(_state, paymentAmount),
                    refetch: (): Promise<void> => refetch(name),
                };
                return acc;
            }, {}),
        [paymentAmount, state, refetch],
    );
}
